import Box from 'components/Box';
import Button from 'components/Button';
import Text from 'components/Text';
import styled from 'styled-components';
import OutlineAddStaff from 'assets/Icons/outlineAddStaff';
import settingStaffSelectors from 'features/settingStaff/services/selectors';
import StaffPricingTable from './StaffPricingTable';
import useGetDetailServicePrice from 'features/settingStaff/services/hooks/useGetDetailServicePrice';
import ChangeServiceModal, {
  ChangeServiceModalRef,
} from 'features/ticketList/pages/TicketDetailsPage/components/ChangeServiceModal';
import { useEffect, useRef, useState } from 'react';
import { IServiceItemData } from 'services/shop/types/categories';
import settingApis from 'features/settingStaff/services/apis';
import { toast } from 'react-toastify';
import { getMessageError, IResponseDataBody } from 'services/response';
import { IServicePrice } from 'features/settingStaff/services/types/api';
import ModalConfirm, {
  useModalConfirmRef,
} from 'components/Modal/ModalConfirm';
import TrashV2 from 'assets/Icons/trashV2';
import { useAppDispatch } from 'store/hooks';
import cashierActions from 'features/cashier/services/actions';

const StaffPricing = () => {
  const useChangeServiceModalRef = useRef<ChangeServiceModalRef>(null);
  const modalConfirm = useModalConfirmRef();
  const staffSelected = settingStaffSelectors.selectedStaffData.data();
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();

  const { detailServicePrice, mutate } = useGetDetailServicePrice(
    staffSelected.id,
  );

  const [tableData, setTableData] = useState<IServicePrice[]>([]);

  useEffect(() => {
    setTableData(
      detailServicePrice?.map((item) => ({
        serviceId: item.serviceId,
        serviceName: item.serviceName,
        defaultPrice: item.defaultPrice,
        price: item.price,
      })) ?? [],
    );
  }, [detailServicePrice]);

  const handleAddServices = (
    _serviceId?: string,
    _serviceObject?: IServiceItemData,
    _serviceIds?: string[],
    serviceObjects?: IServiceItemData[],
  ) => {
    if (!serviceObjects) return;

    setTableData((prev) => {
      const existingMap = new Map(prev.map((item) => [item.serviceId, item]));

      return serviceObjects.map((item) => {
        const existingService = existingMap.get(item.id);

        if (existingService) {
          return existingService;
        }

        return {
          serviceId: item.id,
          serviceName: item.serviceName,
          defaultPrice: item.priceSell,
          price: item.priceSell,
        };
      });
    });
  };

  const handleChangePrice = (serviceId: string, newPrice: number) => {
    setTableData((prevData) =>
      prevData.map((item) =>
        item.serviceId === serviceId ? { ...item, price: newPrice } : item,
      ),
    );
  };

  const handleDelete = (serviceId: string) => {
    setTableData((prevData) =>
      prevData.filter((item) => item.serviceId !== serviceId),
    );
  };

  const handleDeleteSelected = async (selectedServiceIds: string[]) => {
    const payload = tableData
      .filter((item) => !selectedServiceIds.includes(item.serviceId))
      .map((item) => ({
        serviceId: item.serviceId,
        price: item.price,
      }));

    try {
      const response: IResponseDataBody<boolean> = await settingApis.updateServicePrice({
        staffId: staffSelected.id,
        services: payload,
      });

      if (response.data.data) {
        toast.success('Selected services deleted successfully');
        dispatch(cashierActions.getCustomServicePricesByStaff.fetch());
        mutate(staffSelected.id);
      }
    } catch (error) {
      toast.error(
        getMessageError(error) || 'Failed to delete selected services',
      );
    }
  };

  const handleDeleteSelectedConfirm = () => {
    modalConfirm.current?.open(
      `Do you want to delete ${selectedServiceIds.length} selected services?`,
      () => {
        handleDeleteSelected(selectedServiceIds);
        setSelectedServiceIds([]);
      },
    );
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const payload = tableData.map((item) => ({
        serviceId: item.serviceId,
        price: item.price,
      }));

      const response: IResponseDataBody<boolean> =
        await settingApis.updateServicePrice({
          staffId: staffSelected.id,
          services: payload,
        });

      if (response.data.data) {
        toast.success('Services Pricing updated successfully');
        dispatch(cashierActions.getCustomServicePricesByStaff.fetch());
        mutate(staffSelected.id);
      }
    } catch (error) {
      toast.error(
        getMessageError(error) || 'Failed to update Services Pricing',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Box
        width='100%'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
      >
        <Box display='flex' flexDirection='column' gap='1'>
          <Text variant='H5'>Staff Pricing</Text>
          <Text variant='H8' color='text_3_v2'>
            Merge custom service prices for this staff. Only servies with a
            custom price are shown.
          </Text>
        </Box>
        <Box display='flex' gap='3'>
          <Button
            ntype='CANCEL'
            iconElement={<TrashV2 width={24} height={24} />}
            size='middle'
            onClick={handleDeleteSelectedConfirm}
            cardCashier
            disabled={selectedServiceIds.length === 0}
            width='140px'
          >
            Delete
          </Button>
          <Button
            ntype='PRIMARY'
            iconElement={<OutlineAddStaff width={24} height={24} />}
            size='middle'
            onClick={() =>
              useChangeServiceModalRef.current?.open(
                handleAddServices,
                undefined,
                undefined,
                tableData.map((item) => item.serviceId),
              )
            }
            cardCashier
          >
            Add Services
          </Button>
          <Button
            ntype='TERTIARY'
            size='middle'
            onClick={handleSave}
            cardCashier
            width='140px'
            disabled={tableData.length === 0 || loading}
          >
            Save
          </Button>
        </Box>
      </Box>

      <StaffPricingTable
        data={tableData}
        onChangePrice={handleChangePrice}
        onDelete={handleDelete}
        selectedServiceIds={selectedServiceIds}
        onSelectionChange={setSelectedServiceIds}
      />
      <ChangeServiceModal ref={useChangeServiceModalRef} isMultiple />
      <ModalConfirm ref={modalConfirm} />
    </Container>
  );
};
export default StaffPricing;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  font-family: Poppins;

  .CONTENT_2 {
    font-size: 20px;
  }
`;
